// The product-type tree (API catalog lib/product-types, `book {productTypes}`), read once a session and shared
// by every browser on the page; `refreshProductTypes()` after a type is proposed. Helpers: a node's name in a
// language, and the search the browser runs over names and keywords.
import { useEffect, useMemo, useState } from "react"
import { ProductTypes } from "../api"

// the languages the browser reads (Bulgarian first: the store's)
export const LANGUAGES = ["bg", "en"]

let cached = null
let inFlight = null
const listeners = new Set()

const load = (force = false) => {
  if (cached && !force) return Promise.resolve(cached)
  if (inFlight && !force) return inFlight
  inFlight = ProductTypes()
    .then((res) => {
      inFlight = null
      cached = Array.isArray(res?.list) ? res.list : []
      for (const listener of listeners) listener(cached)
      return cached
    })
    .catch(() => {
      inFlight = null
      return cached || []
    })
  return inFlight
}

// the tree's root (API lib/product-types ROOT): its profile is the one of a product no type above defines one for
export const ROOT = "product-types"

/**
 * The profile id (a content type's contentId) a product of type `slug` takes, as the API resolves it
 * (lib/product-profiles profileIdOfType): the type's own or the nearest one above it, else the root's; no
 * type, or one not in the tree, none (the store's own items).
 */
export const profileIdOf = (bySlug, slug) => {
  if (!slug) return ""
  if (slug === ROOT) return ROOT
  const node = bySlug?.get(slug)
  return node ? node.effectiveProfile || ROOT : ""
}

/**
 * A store products query for the ones that take one of the profiles `ids`: their types' slugs — and, the
 * root's profile among them, the products filed at the root (API lib/product-profiles productFilter).
 */
export const productTypeQuery = (list, ids) => {
  const wanted = new Set(ids || [])
  const slugs = (list || []).filter((node) => wanted.has(node.effectiveProfile || ROOT)).map((node) => node.slug)
  return { productType: { $in: wanted.has(ROOT) ? [...slugs, ROOT] : slugs } }
}

/** Read the tree again (a type proposed or approved). */
export const refreshProductTypes = () => load(true)

/** A node's name in `language`, else in English, else in any. */
export const nameOf = (node, language = "bg") => (node ? node.names?.[language] || node.names?.en || node.names?.bg || Object.values(node.names || {})[0] || node.slug : "")

const fold = (value) =>
  String(value || "")
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .trim()

/**
 * The types matching `query`, best first: a name in the language starting with it, a name containing it, a
 * keyword, then the same in the other languages. Leaves before groups at the same rank.
 */
export function searchTypes(list, query, language = "bg") {
  const q = fold(query)
  if (!q) return []
  // how well each node's own names and keywords match
  const scores = new Map()
  for (const node of list) {
    const own = fold(node.names?.[language])
    const others = Object.entries(node.names || {}).filter(([lang]) => lang !== language).map(([, text]) => fold(text))
    const words = (node.keywords?.[language] || []).map(fold)
    const otherWords = Object.entries(node.keywords || {}).filter(([lang]) => lang !== language).flatMap(([, list]) => (Array.isArray(list) ? list : []).map(fold))
    let score = 0
    if (own.startsWith(q)) score = 100
    else if (own.includes(q)) score = 80
    else if (words.some((word) => word.startsWith(q))) score = 70
    else if (words.some((word) => word.includes(q))) score = 60
    else if (others.some((text) => text.includes(q))) score = 50
    else if (otherWords.some((word) => word.includes(q))) score = 40
    if (score) scores.set(node.slug, score)
  }
  // only types are answers: a group that matches ("Access cards") stands for the types in it, below the ones
  // that match themselves — never a row of its own next to its namesake type
  const scored = []
  for (const node of list) {
    if (node.childCount) continue
    const own = scores.get(node.slug) || 0
    const inherited = Math.max(0, ...(node.path || []).slice(0, -1).map((slug) => scores.get(slug) || 0)) - 50
    const score = Math.max(own, inherited)
    if (score > 0) scored.push([score, node])
  }
  return scored
    .sort((a, b) => b[0] - a[0] || nameOf(a[1], language).localeCompare(nameOf(b[1], language)))
    .slice(0, 40)
    .map(([, node]) => node)
}

/** The tree: `list`, `bySlug`, `childrenOf` (slug → child slugs, in order), `tops` (the groups). */
export function useProductTypes() {
  const [list, setList] = useState(cached || [])
  const [loading, setLoading] = useState(!cached)
  useEffect(() => {
    let alive = true
    const listener = (next) => alive && setList(next)
    listeners.add(listener)
    load().then((next) => {
      if (!alive) return
      setList(next)
      setLoading(false)
    })
    return () => {
      alive = false
      listeners.delete(listener)
    }
  }, [])
  return useMemo(() => {
    const bySlug = new Map(list.map((node) => [node.slug, node]))
    const childrenOf = new Map()
    const order = (a, b) => (a.sortOrder || 0) - (b.sortOrder || 0) || nameOf(a).localeCompare(nameOf(b))
    for (const node of [...list].sort(order)) {
      const parent = node.parents?.[0]
      if (!parent) continue
      if (!childrenOf.has(parent)) childrenOf.set(parent, [])
      childrenOf.get(parent).push(node.slug)
    }
    const tops = list.filter((node) => !node.parents?.length).sort(order)
    return { list, bySlug, childrenOf, tops, loading }
  }, [list, loading])
}
